import { Tracking as BaseTracking } from '@folklore/tracking';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import isArray from 'lodash-es/isArray';
import uniq from 'lodash-es/uniq';

import TriggersManager from './TriggersManager';

class Tracking extends BaseTracking {
    user: User | null;
    identified: boolean;
    triggers: TriggersManager;
    options: BaseTracking['options'] & {
        user?: User | null;
    };

    constructor(opts = {}) {
        super(opts);

        this.user = null;
        this.identified = false;

        this.triggers = new TriggersManager(this.options);

        const { user = null } = this.options;
        if (user !== null) {
            this.setUser(user);
        }
    }

    pushNow(...args) {
        if (this.disabled) {
            return;
        }
        const push = () => {
            window.dataLayer.push(...args);

            if (typeof this.triggers !== 'undefined') {
                this.triggers.push(...args);
            }
        };
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => push());
        } else {
            setTimeout(() => push(), 500);
        }
    }

    setUser(user) {
        if (user === this.user && this.identified) {
            return;
        }

        this.user = user;
        this.identified = true;

        this.pushEventNow('identify', {
            identified: true,
            userId: user !== null ? user.id : null,
            user_id: user !== null ? user.id : null,
            user,
        });
    }

    trackPageViewFromDocument(document, extraData) {
        const page = this.getPageVariablesFromDocument(document, extraData);
        this.pushEvent('pageView', page);
    }

    trackPageView(data) {
        const page = this.getPageVariables(data);
        this.pushEvent('pageView', page);
    }

    loadImagePixel(url) {
        this.pushEvent('loadImagePixel', {
            pixelImageUrl: url,
        });
    }

    trackPromotion(promotion, action, data = null) {
        const { id = null, handle = null } = promotion;
        if (id === 'consent') {
            return;
        }
        this.trackEvent('Promotion', action, handle || id, null, {
            ...data,
            promotion,
        });
    }

    trackModal(id, action, data) {
        this.trackEvent('Modal', action, id, null, data);
    }

    trackNotification(id, action, data) {
        this.trackEvent('Notification', action, id, null, data);
    }

    trackSearch(query, data = {}) {
        this.trackEvent('Search', 'query', query, null, data);
    }

    trackSubscribe(subscription, source, data = {}) {
        this.trackEvent(
            'Subscription',
            'subscribe',
            isArray(subscription) ? subscription.sort().join(',') : subscription,
            null,
            {
                subscription_source: source,
                ...data,
            },
        );
    }

    trackUnsubscribe(subscription, source, data = {}) {
        this.trackEvent(
            'Subscription',
            'unsubscribe',
            isArray(subscription) ? subscription.sort().join(',') : subscription,
            null,
            {
                subscription_source: source,
                ...data,
            },
        );
    }

    trackVideo(
        action,
        {
            platform = null,
            id = null,
            url,
            title = null,
            duration = null,
            currentTime = null,
            thumbnail = null,
        } = {},
    ) {
        this.trackEvent(
            'Video',
            action,
            `${platform !== null ? `${platform}: ` : ''}${title || document.title}${
                id !== null ? ` (${id})` : ''
            }`,
            null,
            {
                videoPlatform: platform,
                videoId: id,
                videoUrl: url,
                videoTitle: title,
                videoDuration: duration,
                videoCurrentTime: currentTime !== null ? Math.round(currentTime) : null,
                videoProgress:
                    currentTime !== null && duration !== null && duration > 0
                        ? Math.round((currentTime / duration) * 100)
                        : null,
                videoThumbnail: thumbnail,
            },
        );
    }

    trackAd(action, slot, { size = null } = {}) {
        const unitPath = slot.getAdPath();
        const targeting = slot.getTargeting();
        const adSize = size !== null && isArray(size) ? size.join('x') : size;
        this.trackEvent('Ads', action, `${unitPath}${adSize !== null ? ` ${adSize}` : ''}`, null, {
            adUnitPath: unitPath,
            adSize,
            adPosition: targeting !== null ? targeting.position || null : null,
        });
    }

    trackLink(link, data) {
        this.trackEvent('Navigation', 'click_external', link, null, data);
    }

    getPageVariables(data) {
        const {
            brand = null,
            brands = null,
            section = null,
            categories = null,
            sponsors = null,
            tags = null,
            formats = null,
            ...otherData
        } = data || {};
        return {
            hasPage: true,
            pageTitle: typeof document !== 'undefined' ? document.title : null,
            pageType: null,
            wordpressId: null,
            section: section !== null ? section.handle || section.slug || null : null,
            brand: brand !== null ? brand.handle : null,
            brands:
                brands !== null || brand !== null
                    ? uniq(
                          [...(brands || []), brand]
                              .filter((it) => it !== null)
                              .map(({ handle }) => handle),
                      )
                    : null,
            categories:
                categories !== null ? categories.map(({ slug, handle }) => handle || slug) : null,
            tags: tags !== null ? tags.map(({ slug, handle }) => handle || slug) : null,
            formats: formats !== null ? formats.map(({ slug, handle }) => handle || slug) : null,
            publishedDate: null,
            authors: null,
            sponsors:
                sponsors !== null
                    ? sponsors.map(({ organisation = {} }) => (organisation || {}).handle || null)
                    : null,
            identifier: null,
            identifiers: null,
            document: null,
            documentProgress: 0,
            scrollPercent: 0,
            screenIndex: null,
            serie: null,
            collection: null,
            isTrailer: false,
            trafficLevel: null,
            interestLevel: null,

            ...otherData,
        };
    }

    getPageVariablesFromDocument(document, extraData) {
        const {
            type,
            title,
            slug,
            metadata: {
                trackingSettings = null,
                brands = [],
                categories = [],
                topics = [],
                formats = [],
                tags = null,
                authors = null,
                sponsors = null,
                publishedAt = null,
                collections = null,
            } = {},
            stats: { traffic: trafficLevel = null, interest: interestLevel = null } = {},
            micromag = null,
            identifiers,
            identifier,
        } = document;
        const { data: trackingData = null } = trackingSettings || {};
        const [brand = null] = brands || [];

        const publishedDate =
            publishedAt !== null ? format(parseISO(publishedAt), 'yyyy-MM-dd') : null;
        const serie = (collections || []).reduce((acc, c) => {
            if (acc !== null) {
                return acc;
            }
            const { type, serie = null } = c;
            return (type || '').indexOf('serie') !== -1 ? c : serie;
        }, null);
        const { slug: serieSlug = (type || '').indexOf('serie') !== -1 ? slug : null } =
            serie || {};
        const [{ slug: collectionSlug } = {}] = collections || [];

        return this.getPageVariables({
            pageTitle: title,
            pageType: type,
            brand,
            brands,
            categories: [...(categories || []), ...(topics || [])],
            formats: [...(formats || [])],
            tags,
            publishedDate,
            authors: (authors || []).map(({ slug = null }) => slug).filter((it) => it !== null),
            sponsors,
            micromag,
            identifiers,
            identifier,
            document,
            collection: collectionSlug,
            serie: serieSlug,
            trafficLevel,
            interestLevel,
            ...trackingData,
            ...extraData,
        });
    }
}

export default Tracking;
